/**
 * Choosing one of the 161 catalog exercises.
 *
 * Typing here goes through the same `matchExercise` the bot uses, so "bench" lands on
 * the same movement in both clients — then falls back to a plain substring scan so the
 * whole catalog stays browsable, which is the one thing shorthand cannot do. Grouped by
 * muscle group, because that is how a lifter looks for a movement they half-remember.
 *
 * Searching only works when you have a name in mind. Half the time in a gym you do not —
 * you know it is a back movement and you will recognise it — and that meant flicking
 * past a hundred rows to reach the group. The dropdown makes that one tap.
 */

import { useEffect, useMemo, useState } from "react";
import type { CatalogExercise, MuscleGroup } from "@altrtrack/core";
import { useCatalogRevision } from "../data.js";
import { CATALOG, MUSCLE_GROUPS, installedAliases, matchExercise, normalise } from "@altrtrack/core";

export const GROUP_NAMES: Record<MuscleGroup, string> = {
  chest: "Chest",
  back: "Back",
  shoulders: "Shoulders",
  biceps: "Biceps",
  triceps: "Triceps",
  forearms: "Forearms",
  quads: "Quads",
  hamstrings: "Hamstrings",
  glutes: "Glutes",
  calves: "Calves",
  core: "Core",
  fullBody: "Full body",
};

export function ExercisePicker({
  onPick,
  onCancel,
}: {
  onPick: (exercise: CatalogExercise) => void;
  onCancel: () => void;
}) {
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState<MuscleGroup | "">("");
  // The catalog can grow while the picker is open (B-66); the memo reads it by reference.
  const revision = useCatalogRevision();

  const results = useMemo(() => {
    const term = search.trim();
    if (!term) return null;

    // The matcher's own answer first — it knows the aliases and handles a typo.
    const matched = matchExercise(term);
    const preferred =
      matched.kind === "match" ? [matched.exercise] : matched.kind === "ambiguous" ? matched.candidates : [];

    const needle = normalise(term);
    // An added exercise's other names (B-66) are found by part too, as its name is:
    // "meadows" should reach the one whose other name is "meadows row".
    const byAlias = new Set(
      installedAliases()
        .filter(([alias]) => normalise(alias).includes(needle))
        .map(([, id]) => id),
    );
    const rest = CATALOG.filter(
      (e) =>
        (normalise(e.name).includes(needle) || byAlias.has(e.id)) && !preferred.some((p) => p.id === e.id),
    );
    // The filter narrows the matcher's answer too — otherwise picking "Back" and then
    // typing would show chest movements again, which reads as the filter being ignored.
    return [...preferred, ...rest].filter((e) => !group || e.muscleGroup === group);
    // `revision` stands for CATALOG, which is read by reference.
  }, [search, group, revision]);

  // Browsing, filtered. One group when one is chosen, all of them otherwise.
  const browsing = group ? [group] : MUSCLE_GROUPS;

  // A sheet, not an inline block: it used to be a 416px inner scroll region nested
  // inside the scrolling page, clipping its own rows mid-word. Escape closes it, which
  // is the one affordance a full-screen overlay owes a keyboard.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="picker" role="dialog" aria-modal="true" aria-label="Choose an exercise">
      <div className="picker-head">
        <input
          className="input"
          type="search"
          autoFocus
          value={search}
          placeholder={`Search ${CATALOG.length} exercises`}
          aria-label="Search exercises"
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="btn btn-small" type="button" onClick={onCancel}>
          Cancel
        </button>
        <select
          className="select picker-filter"
          aria-label="Show one muscle group only"
          value={group}
          onChange={(e) => setGroup(e.target.value as MuscleGroup | "")}
        >
          <option value="">All muscle groups</option>
          {MUSCLE_GROUPS.map((g) => (
            <option key={g} value={g}>
              {GROUP_NAMES[g]}
            </option>
          ))}
        </select>
      </div>

      <div className="picker-body">

      {results ? (
        results.length === 0 ? (
          <p className="sub">
            Nothing matches “{search.trim()}”
            {group && ` in ${GROUP_NAMES[group]}`}. The list has {CATALOG.length} movements —
            try a shorter word{group && ", or all muscle groups"}.
          </p>
        ) : (
          <ul className="picker-list">
            {results.map((e) => (
              <PickerItem key={e.id} exercise={e} onPick={onPick} showGroup />
            ))}
          </ul>
        )
      ) : (
        browsing.map((each) => (
          <section key={each} className="factor-group">
            <h3 className="label picker-group-name">{GROUP_NAMES[each]}</h3>
            <ul className="picker-list">
              {CATALOG.filter((e) => e.muscleGroup === each).map((e) => (
                <PickerItem key={e.id} exercise={e} onPick={onPick} />
              ))}
            </ul>
          </section>
        ))
      )}
      </div>
    </div>
  );
}

function PickerItem({
  exercise,
  onPick,
  showGroup = false,
}: {
  exercise: CatalogExercise;
  onPick: (exercise: CatalogExercise) => void;
  showGroup?: boolean;
}) {
  return (
    <li>
      <button className="picker-item" type="button" onClick={() => onPick(exercise)}>
        <span>{exercise.name}</span>
        <span className="sub">
          {showGroup && GROUP_NAMES[exercise.muscleGroup]}
          {showGroup && exercise.measure === "duration" && " · "}
          {exercise.measure === "duration" && "seconds"}
        </span>
      </button>
    </li>
  );
}
